// src/pages/masterdata/project/ProjectList.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Eye, Edit2, Trash2, FileText, Package, ChevronDown, ChevronRight, ArrowLeft, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import ExpandableDataTable from '../../../components/common/ExpandableDataTable';
import ProjectForm from './ProjectForm';
import projectService, { 
  getProjectTypeBadgeClass, 
  getStatusBadgeClass,
  getStatusDisplayName,
  calculateDaysRemaining,
  formatCurrency
} from '../../../services/projectService';

const ProjectList = () => {
  const [view, setView] = useState('list'); // 'list', 'add', 'edit', 'detail'
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [projectDetails, setProjectDetails] = useState({}); // Store fetched project details with sites/segments
  const [loadingDetails, setLoadingDetails] = useState(new Set()); // Track which projects are being loaded

  // Pagination & Sorting State
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'createdAt', direction: 'DESC' });
  const [searchValue, setSearchValue] = useState('');
  const [filterType, setFilterType] = useState('ALL');

  // Fetch projects on mount
  useEffect(() => {
    fetchProjects();
  }, []);

  // Fetch all projects
  const fetchProjects = async () => {
    setIsLoading(true);
    try {
      const data = await projectService.getAllProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      // Error already handled by axios interceptor
    } finally {
      setIsLoading(false);
    }
  };

  // Filter projects based on search and type filter
  const filteredProjects = projects.filter(project => {
    const matchesSearch = 
      project.projectName.toLowerCase().includes(searchValue.toLowerCase()) ||
      project.projectCode.toLowerCase().includes(searchValue.toLowerCase()) ||
      project.customerName.toLowerCase().includes(searchValue.toLowerCase()) ||
      (project.spks && project.spks.some(spk => 
        spk.spkNumber.toLowerCase().includes(searchValue.toLowerCase())
      ));
    
    const matchesFilter = filterType === 'ALL' || project.projectType === filterType;
    
    return matchesSearch && matchesFilter;
  });

  // Sort projects
  const sortedProjects = [...filteredProjects].sort((a, b) => {
    let aValue = a[sortConfig.sortBy];
    let bValue = b[sortConfig.sortBy];
    
    // Handle nested values
    if (sortConfig.sortBy === 'customerName') {
      aValue = a.customerName;
      bValue = b.customerName;
    }
    
    if (aValue < bValue) return sortConfig.direction === 'ASC' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'ASC' ? 1 : -1;
    return 0;
  });

  // Paginate projects
  const startIndex = currentPage * pageSize;
  const paginatedProjects = sortedProjects.slice(startIndex, startIndex + pageSize);

  // Toggle expand row for sites/segments
  // Toggle expand row and fetch details if needed
  const toggleExpandRow = async (projectId) => {
    const isCurrentlyExpanded = expandedRows.has(projectId);
    
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (isCurrentlyExpanded) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });

    // Fetch project details if expanding and not already loaded
    if (!isCurrentlyExpanded && !projectDetails[projectId]) {
      setLoadingDetails(prev => new Set(prev).add(projectId));
      
      try {
        // Fetch project detail WITH sites/segments from new endpoint
        const projectDetail = await projectService.getProjectByIdWithDetails(projectId);
        
        setProjectDetails(prev => ({
          ...prev,
          [projectId]: projectDetail
        }));
      } catch (error) {
        console.error('Error fetching project details:', error);
        toast.error('Failed to load project details');
        // Collapse row on error
        setExpandedRows(prev => {
          const newSet = new Set(prev);
          newSet.delete(projectId);
          return newSet;
        });
      } finally {
        setLoadingDetails(prev => {
          const newSet = new Set(prev);
          newSet.delete(projectId);
          return newSet;
        });
      }
    }
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'no',
      label: 'No',
      sortable: false,
      width: 'w-16',
      render: (row, index) => (
        <span className="font-medium text-gray-900">
          {startIndex + index + 1}
        </span>
      )
    },
    {
      key: 'projectCode',
      label: 'Project Code',
      sortable: true,
      render: (row) => (
        <div>
          <span className="font-semibold text-blue-600">{row.projectCode}</span>
          <div className="text-xs text-gray-500 mt-1">ID: {row.id}</div>
        </div>
      )
    },
    {
      key: 'projectName',
      label: 'Project Name',
      sortable: true,
      nowrap: false,
      render: (row) => (
        <div>
          <div className="font-semibold text-gray-900">{row.projectName}</div>
          {row.spks && row.spks.length > 0 && (
            <div className="text-xs text-blue-600 mt-1">
              SPK: {row.spks[0].spkNumber}
              {row.spks.length > 1 && ` +${row.spks.length - 1} more`}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'customerName',
      label: 'Customer',
      sortable: true,
      render: (row) => (
        <span className="text-gray-900">{row.customerName}</span>
      )
    },
    {
      key: 'projectType',
      label: 'Type',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getProjectTypeBadgeClass(row.projectType)}`}>
          {row.projectType}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(row.status)}`}>
          {getStatusDisplayName(row.status)}
        </span>
      )
    },
    {
      key: 'items',
      label: 'Items',
      sortable: false,
      render: (row) => {
        const itemCount = row.projectType === 'TOWER' ? row.towerSiteCount : row.foSegmentCount;
        const itemLabel = row.projectType === 'TOWER' ? 'Sites' : 'Segments';
        
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpandRow(row.id);
            }}
            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded-lg transition-colors"
          >
            {expandedRows.has(row.id) ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            <span className="font-semibold">
              {itemCount} {itemLabel}
            </span>
          </button>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleView(row)}
            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleEdit(row)}
            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit2 className="w-5 h-5" />
          </button>
          <button
            onClick={() => handleDelete(row)}
            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      )
    }
  ];

  // Handlers
  const handleAddNew = () => {
    setView('add');
    setSelectedProject(null);
  };

  const handleView = async (project) => {
    try {
      // Fetch full project details including sites/segments
      const projectDetail = await projectService.getProjectByIdWithDetails(project.id);
      setSelectedProject(projectDetail);
      setView('detail');
    } catch (error) {
      console.error('Error fetching project details:', error);
      toast.error('Failed to load project details');
    }
  };

  const handleEdit = (project) => {
    setSelectedProject(project);
    setView('edit');
  };

  const handleDelete = async (project) => {
    if (window.confirm(`Are you sure you want to delete project ${project.projectName}?\n\nThis action will soft-delete the project.`)) {
      try {
        await projectService.softDeleteProject(project.id);
        toast.success('Project deleted successfully!');
        fetchProjects(); // Refresh list
      } catch (error) {
        console.error('Error deleting project:', error);
        // Error already handled by axios interceptor
      }
    }
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedProject(null);
    fetchProjects(); // Refresh list when coming back
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setCurrentPage(0);
  };

  // Render expanded content (sites/segments)
  const renderExpandedContent = (project) => {
    const itemCount = project.projectType === 'TOWER' ? project.towerSiteCount : project.foSegmentCount;
    const itemLabel = project.projectType === 'TOWER' ? 'Tower Sites' : 'FO Segments';
    const isLoadingDetail = loadingDetails.has(project.id);
    const projectDetail = projectDetails[project.id];
    
    return (
      <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-gray-50">
        <div className="ml-8">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
              <Package className="w-4 h-4 text-red-600" />
              {itemLabel} ({itemCount})
            </h4>
            <button
              onClick={() => handleView(project)}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
            >
              <Eye className="w-3 h-3" />
              View Full Details
            </button>
          </div>
          
          {isLoadingDetail ? (
            // Loading state
            <div className="bg-white rounded-lg border border-blue-200 p-8">
              <div className="flex flex-col items-center justify-center space-y-3">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-blue-400 rounded-full animate-bounce"></div>
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-3 h-3 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
                <p className="text-gray-500 text-sm">Loading {itemLabel.toLowerCase()}...</p>
              </div>
            </div>
          ) : projectDetail && project.projectType === 'TOWER' && projectDetail.towerSites ? (
            // TOWER: Show tower sites in grid
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {projectDetail.towerSites.map(site => (
                <div key={site.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Package className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="font-semibold text-sm text-gray-900">{site.siteId || site.siteName}</span>
                  </div>
                  <div className="text-xs text-gray-600 ml-11">{site.regional || site.location || '-'}</div>
                  {site.status && (
                    <span className="inline-block mt-2 ml-11 px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                      {site.status}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : projectDetail && project.projectType === 'FO' && projectDetail.foSegments ? (
            // FO: Show segments in list
            <div className="space-y-2">
              {projectDetail.foSegments.map(segment => (
                <div key={segment.id} className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <Package className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-gray-900">{segment.spanId || segment.segmentId}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {segment.siteNameAB || `${segment.startPoint || ''} → ${segment.endPoint || ''}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {segment.commercialDistance && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                        {segment.commercialDistance} km
                      </span>
                    )}
                    {segment.status && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        {segment.status}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Fallback: No detail loaded yet or empty
            <div className="bg-white rounded-lg border border-blue-200 p-4">
              <div className="text-sm text-gray-600 text-center py-2">
                {project.projectType === 'TOWER' ? (
                  <div className="flex items-center justify-center gap-2">
                    <Package className="w-5 h-5 text-purple-500" />
                    <span>Contains <span className="font-semibold text-purple-600">{itemCount} tower sites</span></span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Package className="w-5 h-5 text-green-500" />
                    <span>Contains <span className="font-semibold text-green-600">{itemCount} FO segments</span></span>
                  </div>
                )}
                <div className="mt-2 text-xs text-gray-500">
                  Click "View Full Details" button above to see complete information
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render Form View
  if (view === 'add' || view === 'edit') {
    return (
      <ProjectForm
        onBack={handleBackToList}
        projectData={selectedProject}
        mode={view}
      />
    );
  }

  // Render Detail View
  if (view === 'detail' && selectedProject) {
    return <ProjectDetail project={selectedProject} onBack={handleBackToList} onEdit={() => handleEdit(selectedProject)} />;
  }

  // Render List View (Default)
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center shadow-md">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Project List</h1>
              <p className="text-gray-600 mt-1">Manage construction projects</p>
            </div>
          </div>
          <button 
            onClick={handleAddNew}
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Project
          </button>
        </div>
      </div>

      {/* Filter by Type */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="flex items-center gap-4">
          <span className="text-sm font-semibold text-gray-700">Filter by Type:</span>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('ALL')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'ALL'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Types ({projects.length})
            </button>
            <button
              onClick={() => setFilterType('TOWER')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'TOWER'
                  ? 'bg-purple-600 text-white'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              TOWER ({projects.filter(p => p.projectType === 'TOWER').length})
            </button>
            <button
              onClick={() => setFilterType('FO')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'FO'
                  ? 'bg-green-600 text-white'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              FO ({projects.filter(p => p.projectType === 'FO').length})
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="relative">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by project name, code, SPK number, or customer..."
            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
          <svg className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* ExpandableDataTable Component */}
      <ExpandableDataTable
        columns={columns}
        data={paginatedProjects}
        currentPage={currentPage}
        pageSize={pageSize}
        totalElements={filteredProjects.length}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSort={handleSort}
        sortConfig={sortConfig}
        isLoading={isLoading}
        expandedRows={expandedRows}
        expandedContent={renderExpandedContent}
        rowClassName="transition-colors"
      />
    </div>
  );
};

// ========================================
// Project Detail Component (Tabs: Info + Sites/Segments)
// ========================================

const ProjectDetail = ({ project, onBack, onEdit }) => {
  const [activeTab, setActiveTab] = useState('info');

  const daysRemaining = calculateDaysRemaining(project.targetEndDate);
  const isOverdue = project.isOverdue;
  const progressPercentage = project.totalProgress || 0;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to list"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Project Details</h1>
              <p className="text-gray-600 mt-1">{project.projectCode}</p>
            </div>
          </div>
          <button 
            onClick={onEdit} 
            className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-3 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            <Edit2 className="w-5 h-5" />
            Edit Project
          </button>
        </div>
      </div>

      {/* Progress & Status Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Progress Card */}
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-blue-100 text-sm font-medium">Progress</span>
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(project.status)}`}>
              {getStatusDisplayName(project.status)}
            </span>
          </div>
          <div className="text-3xl font-bold">{progressPercentage.toFixed(1)}%</div>
          <div className="mt-3 bg-white bg-opacity-20 rounded-full h-2">
            <div 
              className="bg-white rounded-full h-2 transition-all"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Budget Card */}
        <div className="bg-gradient-to-br from-green-600 to-green-700 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-green-100 text-sm font-medium">Total Budget</span>
          </div>
          <div className="text-2xl font-bold">{formatCurrency(project.totalBudget)}</div>
          <div className="text-sm text-green-100 mt-2">
            Actual: {formatCurrency(project.totalActualCost)}
          </div>
        </div>

        {/* Timeline Card */}
        <div className={`bg-gradient-to-br rounded-lg shadow-lg p-6 text-white ${
          isOverdue ? 'from-red-600 to-red-700' : daysRemaining < 30 ? 'from-yellow-500 to-yellow-600' : 'from-purple-600 to-purple-700'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${isOverdue ? 'text-red-100' : daysRemaining < 30 ? 'text-yellow-100' : 'text-purple-100'}`}>
              Days Remaining
            </span>
          </div>
          <div className="text-3xl font-bold">
            {isOverdue ? 'OVERDUE' : daysRemaining !== null ? daysRemaining : '-'}
          </div>
          <div className={`text-sm mt-2 ${isOverdue ? 'text-red-100' : daysRemaining < 30 ? 'text-yellow-100' : 'text-purple-100'}`}>
            Target: {new Date(project.targetEndDate).toLocaleDateString('id-ID')}
          </div>
        </div>

        {/* Items Card */}
        <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-100 text-sm font-medium">
              {project.projectType === 'TOWER' ? 'Tower Sites' : 'FO Segments'}
            </span>
          </div>
          <div className="text-3xl font-bold">
            {project.projectType === 'TOWER' ? project.towerSiteCount : project.foSegmentCount}
          </div>
          <div className="text-sm text-gray-200 mt-2">
            Type: {project.projectType}
          </div>
        </div>
      </div>

      {/* Detail Card with Tabs */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-red-50">
          <div className="flex px-6">
            <button
              onClick={() => setActiveTab('info')}
              className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'info' 
                  ? 'border-red-600 text-red-600' 
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              Project Info
            </button>
            <button
              onClick={() => setActiveTab('items')}
              className={`px-6 py-4 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === 'items' 
                  ? 'border-red-600 text-red-600' 
                  : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
              }`}
            >
              {project.projectType === 'TOWER' ? 'Tower Sites' : 'FO Segments'}
              <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                activeTab === 'items' ? 'bg-red-100 text-red-800' : 'bg-gray-200 text-gray-700'
              }`}>
                {project.projectType === 'TOWER' ? project.towerSiteCount : project.foSegmentCount}
              </span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'info' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Info */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Project Code</label>
                <p className="text-lg font-semibold text-gray-900">{project.projectCode}</p>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Project Type</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getProjectTypeBadgeClass(project.projectType)}`}>
                  {project.projectType}
                </span>
              </div>

              <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Project Name</label>
                <p className="text-lg font-medium text-gray-900">{project.projectName}</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Customer</label>
                <p className="text-lg font-medium text-gray-900">{project.customerName}</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Status</label>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${getStatusBadgeClass(project.status)}`}>
                  {getStatusDisplayName(project.status)}
                </span>
              </div>

              {/* Dates */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Start Date</label>
                <p className="text-base text-gray-900">{new Date(project.startDate).toLocaleDateString('id-ID')}</p>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <label className="block text-sm font-semibold text-gray-500 mb-2">Target End Date</label>
                <p className="text-base text-gray-900">{new Date(project.targetEndDate).toLocaleDateString('id-ID')}</p>
              </div>

              {/* SPK Info */}
              {project.spks && project.spks.length > 0 && (
                <div className="md:col-span-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <label className="block text-sm font-semibold text-blue-900 mb-3">Related SPKs</label>
                  <div className="space-y-2">
                    {project.spks.map(spk => (
                      <div key={spk.id} className="bg-white p-3 rounded border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-semibold text-blue-600">{spk.spkNumber}</span>
                            <span className="text-sm text-gray-600 ml-2">• {spk.projectName}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(spk.spkDate).toLocaleDateString('id-ID')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Description & Notes */}
              {project.description && (
                <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-500 mb-2">Description</label>
                  <p className="text-base text-gray-900">{project.description}</p>
                </div>
              )}

              {project.notes && (
                <div className="md:col-span-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <label className="block text-sm font-semibold text-gray-500 mb-2">Notes</label>
                  <p className="text-base text-gray-900">{project.notes}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'items' && (
            <div>
              {project.projectType === 'TOWER' && project.towerSites && project.towerSites.length > 0 ? (
                /* Tower Sites Table */
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">No</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Site ID</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Site Name</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Regional</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Tower Height</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Progress</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Budget</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {project.towerSites.map((site, index) => (
                        <tr key={site.id} className="hover:bg-blue-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                              {site.siteId}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{site.siteName}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{site.regional}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{site.towerHeight}m</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{site.typeSiteContract}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(site.status)}`}>
                              {getStatusDisplayName(site.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[60px]">
                                <div 
                                  className="bg-blue-600 rounded-full h-2 transition-all"
                                  style={{ width: `${site.progressPercentage || 0}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium text-gray-700 min-w-[40px]">
                                {(site.progressPercentage || 0).toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formatCurrency(site.totalBudget || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : project.projectType === 'FO' && project.foSegments && project.foSegments.length > 0 ? (
                /* FO Segments Table */
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gradient-to-r from-red-600 to-red-500 text-white">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">No</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Span ID</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Segment ID</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Site A & B</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Main Area</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Kota</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Distance (m)</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Ring Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Progress</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase">Budget</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {project.foSegments.map((segment, index) => (
                        <tr key={segment.id} className="hover:bg-red-50 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                              {segment.spanId}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{segment.segmentId || '-'}</td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 max-w-xs truncate" title={segment.siteNameAB}>
                            {segment.siteNameAB}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{segment.mainArea}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{segment.kota}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                            {(segment.commercialDistance || 0).toLocaleString('id-ID')}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                              {segment.ringType}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadgeClass(segment.status)}`}>
                              {getStatusDisplayName(segment.status)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-[60px]">
                                <div 
                                  className="bg-red-600 rounded-full h-2 transition-all"
                                  style={{ width: `${segment.progressPercentage || 0}%` }}
                                />
                              </div>
                              <span className="text-xs font-medium text-gray-700 min-w-[40px]">
                                {(segment.progressPercentage || 0).toFixed(1)}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formatCurrency(segment.totalBudget || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                /* Empty State */
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">
                    No {project.projectType === 'TOWER' ? 'Tower Sites' : 'FO Segments'} found
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    This project has no items yet
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectList;